<template>
  <section style="width: 100%">
    <v-data-table
      class="safety-elevation-3 table-striped"
      :headers="headers"
      :items="items"
      :sort-by="sortByCategoryLocation ? 'tag' : ''"
      :loading="loading"
      :no-data-text="noDataText"
      :footer-props="{
        'items-per-page-text': `${$t('employees.footer.itemsPerPage')}`,
        'items-per-page-all-text': `${$t('employees.footer.all')}`,
      }"
    >
      <template v-slot:[`item.tag`]="{ item }"
        >{{ item.tag.description || item.tag.name }}
      </template>

      <template v-slot:[`item.mentor`]="{ item }">
        <router-link
          v-if="item.status"
          :to="{ name: 'employee', params: { id: item.mentor.id } }"
        >
          <span
            :style="
              item.mentor.isActive ? '' : 'text-decoration: line-through;'
            "
          >
            {{ item.mentor | nameId }}
          </span>
        </router-link>
        <i v-else>{{ $t("profile.mentorship.missing") }}</i>
      </template>
      <template v-slot:[`item.employee`]="{ item }">
        <router-link
          v-if="item.status"
          :to="{ name: 'employee', params: { id: item.employee.id } }"
        >
          {{ item.employee | nameId }}
        </router-link>
        <i v-else>{{ $t("profile.mentorship.missing") }}</i>
      </template>

      <template v-slot:[`item.startedAt`]="{ item }">
        <span v-if="item.startedAt">
          {{ item.startedAt | date }}
        </span>
        <span v-else>-</span>
      </template>

      <template v-slot:[`item.endsAt`]="{ item }">
        <span v-if="item.activeSince">
          {{ item.activeSince | date }}
        </span>
        <span v-else-if="item.endsAt" class="text--disabled">
          {{ item.endsAt | date }}
        </span>
        <span v-else>-</span>
      </template>

      <template v-slot:[`item.duration`]="{ item }">
        <span v-if="item.duration">
          {{
            $tc(
              "profile.mentorship.duration",
              Math.ceil(item.duration / (60 * 60 * 24))
            )
          }}
        </span>
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <v-chip
          small
          :class="
            item.signature && item.signature.type == 'confirm'
              ? item.signature.revoke
                ? 'deep-orange lighten-4 error--text'
                : 'green lighten-4 success--text'
              : 'orange lighten-4 warning--text'
          "
          class="text--darken-1 text-uppercase"
        >
          {{
            item.signature
              ? item.signature.type == "confirm" && item.signature.revoke
                ? $t(`profile.mentorship.${item.signature.revoke.type}.status`)
                : $t(`profile.mentorship.${item.signature.type}.status`)
              : $t(`profile.mentorship.noSignature`)
          }}
        </v-chip>
        <v-spacer></v-spacer>
        <v-chip small v-if="item.fromOldAccount">
          {{ $t("profile.mentorship.fromOldAccount") }}
        </v-chip>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <span v-if="!item.signature && role('employee')">{{
          $t(`profile.mentorship.noSignatureAction`)
        }}</span>

        <span
          v-if="
            item.status == 'accept' &&
              item.actions &&
              !item.mentorshipActionsStats.isAllConfirmed
          "
        >
          {{
            $t(`profile.mentorship.${item.status}.statusActionsOngoing`) +
              " " +
              item.mentorshipActionsStats.confirmedCount +
              "/" +
              item.mentorshipActionsStats.contentLength
          }}
        </span>
        <!-- sign next step -->

        <v-menu
          v-else-if="
            item.next && item.next == 'confirm' && flow[item.next].allowed(item)
          "
        >
          <template v-slot:activator="{ on }">
            <v-btn v-on="on" class="primary accent-4 white--text mr-2" small>
              {{ $t(`profile.mentorship.decision`) }}
            </v-btn>
          </template>

          <v-list>
            <v-list-item @click="sign(item, item.next)">
              {{ $t(`profile.mentorship.${item.next}.create.action`) }}
            </v-list-item>
            <v-list-item @click="fail(item)">
              {{ $t(`profile.mentorship.fail.action`) }}
            </v-list-item>
          </v-list>
        </v-menu>

        <v-btn
          v-else-if="item.next && flow[item.next].allowed(item)"
          @click="sign(item, item.next)"
          class="primary accent-4 white--text mr-2"
          small
          data-test="mentorship-sign"
        >
          {{ $t(`profile.mentorship.${item.next}.create.action`) }}
        </v-btn>

        <span v-else-if="item.next == 'confirm' && !isFeedbackSubmitted(item)">
          {{ $t(`profile.mentorship.feedback.notSubmitted`) }}
        </span>

        <span
          v-else-if="
            item.signature &&
              item.signature.type == 'confirm' &&
              item.signature.revoke
          "
        >
          {{ item.signature.revoke.reason }}
        </span>
        <span v-else-if="item.signature">
          {{ $t(`profile.mentorship.${item.signature.type}.action`) }}
        </span>
      </template>
      <template v-slot:[`item.learningActions`]="{ item }">
        <mentorship-actions-table
          v-if="
            item.status &&
              item.actions &&
              ['assign', 'accept', 'approve', 'affirm', 'confirm'].includes(
                item.status
              )
          "
          :actions="item.actions"
          :menteeId="+item.employee.id"
          :mentorshipMentorId="+item.mentor.id"
          :isOpenBtnColorPrimary="!item.mentorshipActionsStats.isAllConfirmed"
          @refresh="$emit('refresh')"
        />
      </template>
      <template v-slot:[`item.feedbacks`]="{ item }">
        <template
          v-if="
            item.status &&
              ['accept', 'approve', 'affirm', 'confirm'].includes(item.status)
          "
        >
          <mentorship-feedback
            v-for="(feedback, index) in item.feedbacks"
            :key="index"
            :isPreviousStageSubmitted="
              isPreviousFeedbackStageSubmitted(item.feedbacks, index)
            "
            :feedback="feedback"
            :mentorship="item"
            @saved="(feedbacks) => handleFeedbacksChanged(item, feedbacks)"
          />
        </template>
      </template>

      <template v-slot:[`item.menu`]="{ item }">
        <v-menu bottom left>
          <template v-slot:activator="{ on }">
            <v-btn
              icon
              v-on="on"
              :disabled="
                !(
                  (item.status &&
                    (flow[item.status].allowed(item) ||
                      role(
                        'admin',
                        'departmentManager',
                        'manager',
                        'safetyRepresentative'
                      ))) ||
                  (me && item.mentor && me.id == item.mentor.id) ||
                  (!item.id && !role('employee'))
                )
              "
              data-test="mentorship-menu"
            >
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>

          <v-list>
            <!-- deactivate mentorship -->
            <v-list-item
              v-if="
                item.id &&
                  (role(
                    'admin',
                    'departmentManager',
                    'manager',
                    'safetyRepresentative'
                  ) ||
                    (me && item.mentor && me.id == item.mentor.id))
              "
              @click="deactivate(item)"
            >
              {{
                isConfirmed(item)
                  ? $t(`profile.mentorship.deactivate.title`)
                  : $t(`profile.mentorship.deactivate.quit.title`)
              }}
            </v-list-item>
            <!-- revoke last step -->
            <v-list-item
              v-if="
                item.status &&
                  item.status != 'assign' &&
                  flow[item.status].allowed(item) &&
                  !item.signature.revoke &&
                  item.signature.type != 'confirm'
              "
              @click="revoke(item.signature)"
              data-test="mentorship-menu-revoke"
            >
              <v-icon small left>close</v-icon>
              {{ $t(`profile.mentorship.${item.status}.revoke.title`) }}
            </v-list-item>
            <!--  delete confirmation -->
            <v-list-item
              v-if="
                item.signature &&
                  (role('safetyRepresentative') ||
                    (me && me.id == item.signature.employee.id)) &&
                  item.signature.type == 'confirm'
              "
              @click="deleteConfirmation(item.signature)"
            >
              <v-icon small left>close</v-icon>
              {{ $t(`profile.mentorship.${item.status}.revoke.title`) }}
            </v-list-item>

            <!-- transfer mentorship to another tag -->
            <v-list-item
              v-if="role('safetyRepresentative', 'admin') && item.id"
              @click="changeTag(item)"
            >
              {{ $t(`profile.mentorship.changeTag.title`) }}
            </v-list-item>

            <!-- change started date -->
            <v-list-item
              v-if="role('safetyRepresentative', 'admin') && item.status"
              @click="changeStartedAt(item)"
            >
              {{ $t(`profile.mentorship.changeStartedAt.title`) }}
            </v-list-item>

            <!-- change ends date -->
            <v-list-item
              v-if="
                role(
                  'admin',
                  'departmentManager',
                  'manager',
                  'safetyRepresentative'
                ) &&
                  item.status &&
                  !item.activeSince
              "
              @click="changeEndsAt(item)"
            >
              {{ $t(`profile.mentorship.changeEndsAt.title`) }}
            </v-list-item>

            <!-- assign action version -->
            <v-list-item
              v-if="
                (item.status &&
                  item.status != 'confirm' &&
                  me.id == item.mentor.id) ||
                  (role(
                    'admin',
                    'safetyRepresentative',
                    'departmentManager',
                    'manager'
                  ) &&
                    me &&
                    item.employee &&
                    me.id != item.employee.id)
              "
              @click="assignMentorshipActionVersion(item)"
            >
              {{ $t(`mentorship.actions.changeVersion.title`) }}
            </v-list-item>
            <v-list-item
              v-if="me && item.employee && item.id && me.id != item.employee.id"
              @click="changeAssignedFeedbacks(item)"
            >
              {{ $t(`profile.mentorship.feedback.changeStages`) }}
            </v-list-item>
            <v-list-item
              v-if="
                item.id &&
                  role(
                    'admin',
                    'safetyRepresentative',
                    'departmentManager',
                    'manager'
                  ) &&
                  item.status &&
                  item.status != 'confirm'
              "
              @click="changeMentor(item)"
            >
              {{ $t(`profile.mentorship.changeMentor.title`) }}
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-data-table>

    <employee-selector
      :title="$t('profile.mentorship.assign.create.title')"
      :show.sync="mentorSelector.show"
      @change="mentorSelector.callback"
    />

    <change-mentorship-tag
      :employee="employee"
      :oldTag="tagChange.oldTag"
      :show.sync="tagChange.show"
      @change="tagChange.callback"
    />

    <impersonation
      v-if="impersonateDialog.status"
      :show="impersonateDialog.show"
      :title="$t(`profile.mentorship.${impersonateDialog.status}.create.title`)"
      :text="$t(`profile.mentorship.${impersonateDialog.status}.create.text`)"
      :action="
        $t(`profile.mentorship.${impersonateDialog.status}.create.action`)
      "
      @confirm="impersonateDialog.callback"
      @cancel="impersonateDialog.show = false"
    />

    <revoke-modal
      v-if="revokeDialog.status"
      :show="revokeDialog.show"
      :title="$t(`profile.mentorship.${revokeDialog.status}.revoke.title`)"
      :text="$t(`profile.mentorship.${revokeDialog.status}.revoke.text`)"
      :action="$t(`profile.mentorship.${revokeDialog.status}.revoke.action`)"
      @confirm="revokeDialog.callback"
      @cancel="revokeDialog.show = false"
    />

    <revoke-modal
      v-if="deactivateDialog.show"
      :show="deactivateDialog.show"
      :title="
        isConfirmed(deactivateDialog.item)
          ? $t(`profile.mentorship.deactivate.title`)
          : $t(`profile.mentorship.deactivate.quit.title`)
      "
      :text="
        isConfirmed(deactivateDialog.item)
          ? $t(`profile.mentorship.deactivate.text`)
          : $t(`profile.mentorship.deactivate.quit.text`)
      "
      :action="
        isConfirmed(deactivateDialog.item)
          ? $t(`profile.mentorship.deactivate.action`)
          : $t(`profile.mentorship.deactivate.quit.action`)
      "
      @confirm="deactivateDialog.callback"
      @cancel="deactivateDialog.show = false"
    />

    <revoke-modal
      v-if="failDialog.show"
      :show="failDialog.show"
      :title="$t(`profile.mentorship.fail.title`)"
      :text="$t(`profile.mentorship.fail.text`)"
      :action="$t(`profile.mentorship.fail.action`)"
      @confirm="failDialog.callback"
      @cancel="failDialog.show = false"
    />

    <mentorship-action-assign-version
      :assignActionVersion="assignActionVersion"
      @done="(isUpdated) => handleAssignActionVersionDone(isUpdated)"
    />

    <change-mentorship-feedback
      :show.sync="feedbacksChange.show"
      :assignedFeedbackForms="feedbacksChange.assignedFeedbackForms"
      :tag="feedbacksChange.tag"
      @change="feedbacksChange.callback"
    />

    <date-picker
      :show.sync="changeStartedAtDialog.show"
      :date="changeStartedAtDialog.date"
      :title="$t(`profile.mentorship.changeStartedAt.title`)"
      @change="changeStartedAtDialog.callback"
    />

    <date-picker
      :show.sync="changeEndsAtDialog.show"
      :date="changeEndsAtDialog.date"
      :title="$t(`profile.mentorship.changeEndsAt.title`)"
      @change="changeEndsAtDialog.callback"
    />
  </section>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import Impersonation from "@/components/Impersonation";
import RevokeModal from "@/components/RevokeModal";
import EmployeeSelector from "@/components/EmployeeSelector";
import ChangeMentorshipTag from "@/components/employee/ChangeMentorshipTag";
import MentorshipActionsTable from "@/components/mentorship/MentorshipActionsTable";
import MentorshipActionAssignVersion from "@/components/mentorship/control/MentorshipActionAssignVersion";
import MentorshipFeedback from "@/components/mentorship/MentorshipFeedback";
import ChangeMentorshipFeedback from "@/components/mentorship/control/feedback/ChangeMentorshipFeedback";
import DatePicker from "@/components/DatePicker";

import AcceptMentorshipQuery from "@/graphql/AcceptMentorship.gql";
import ApproveMentorshipQuery from "@/graphql/ApproveMentorship.gql";
import AffirmMentorshipQuery from "@/graphql/AffirmMentorship.gql";
import ConfirmMentorshipQuery from "@/graphql/ConfirmMentorship.gql";
import RevokeSignatureQuery from "@/graphql/RevokeSignature.gql";
import DeleteMentorshipConfirmationQuery from "@/graphql/DeleteMentorshipConfirmation.gql";
import FailMentorshipQuery from "@/graphql/mutation/mentorship/FailMentorship.gql";
import ChangeMentorshipTagQuery from "@/graphql/mutation/profile/ChangeMentorshipTag.gql";
import ChangeAssignedMentorshipFeedbackFormsQuery from "@/graphql/mutation/mentorshipFeedback/ChangeAssignedMentorshipFeedbackForms.gql";
import DeactivateMentorshipQuery from "@/graphql/mutation/mentorship/DeactivateMentorship.gql";
import ChangeMentorshipMentorQuery from "@/graphql/mutation/mentorship/ChangeMentorshipMentor.gql";
import ChangeMentorshipStartedAt from "@/graphql/mutation/mentorship/ChangeMentorshipStartedAt.gql";
import ChangeMentorshipEndsAt from "@/graphql/mutation/mentorship/ChangeMentorshipEndsAt.gql";
import MyOldAccountsQuery from "@/graphql/queries/MyOldAccounts.gql";

import {
  getMentorshipActionsStats,
  getSignatureState,
  matchesUserOrOldID,
} from "@/utils/mentorshipActions";

export default {
  components: {
    Impersonation,
    RevokeModal,
    EmployeeSelector,
    ChangeMentorshipTag,
    MentorshipActionsTable,
    MentorshipActionAssignVersion,
    MentorshipFeedback,
    ChangeMentorshipFeedback,
    DatePicker,
  },

  props: {
    employee: {
      type: Object,
    },
    mentorships: {
      type: Array,
    },
    id: {
      type: [Number, String],
      validator: function(value) {
        if (typeof value === "number") {
          return true;
        } else if (typeof value === "string") {
          return !isNaN(Number(value));
        }
        return false;
      },
      required: true,
    },
    mentorView: {
      type: Boolean,
      default: false,
    },
    showMentorColumn: {
      type: Boolean,
      default: false,
    },
    sortByCategoryLocation: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
    },
    noDataText: {
      type: String,
    },
  },

  data() {
    return {
      assignActionVersion: null,
      flow: {
        assign: {
          allowed: (item) =>
            this.role(
              "admin",
              "departmentManager",
              "manager",
              "safetyRepresentative"
            ) ||
            (item.mentor && this.me && this.me.id == item.mentor.id),
        },
        accept: {
          allowed: (item) => this.me && this.me.id == item.mentor.id,
          query: AcceptMentorshipQuery,
        },
        approve: {
          allowed: (item) => this.me && this.me.id == item.mentor.id,
          query: ApproveMentorshipQuery,
        },
        affirm: {
          allowed: (item) =>
            this.me &&
            matchesUserOrOldID(
              this.me,
              Number(item.employee.id),
              this.myOldAccounts
            ),
          query: AffirmMentorshipQuery,
        },
        confirm: {
          allowed: (item) =>
            this.role(
              "manager",
              "admin",
              "departmentManager",
              "safetyRepresentative"
            ) && this.isFeedbackSubmitted(item),
          query: ConfirmMentorshipQuery,
          noPassword: true,
        },
      },

      revStatuses: ["confirm", "affirm", "approve", "accept", "assign"],

      impersonateDialog: {
        show: false,
        status: null,
        callback: () => {},
      },

      revokeDialog: {
        show: false,
        status: null,
        callback: () => {},
      },

      deactivateDialog: {
        show: false,
        item: null,
        callback: () => {},
      },

      failDialog: {
        show: false,
        callback: () => {},
      },

      mentorSelector: {
        show: false,
        oldTag: null,
        callback: () => {},
      },

      tagChange: {
        show: false,
        callback: () => {},
      },

      feedbacksChange: {
        show: false,
        callback: (id, toAssignMentorshipFeedbackIds) =>
          this.changeAssignedFeedback(id, toAssignMentorshipFeedbackIds),
      },

      changeStartedAtDialog: {
        show: false,
        date: null,
        callback: () => {},
      },

      changeEndsAtDialog: {
        show: false,
        date: null,
        callback: () => {},
      },
    };
  },

  computed: {
    ...mapGetters(["role"]),
    ...mapState(["me"]),

    headers() {
      const h = [
        {
          text: this.$i18n.t("profile.mentorship.column.tag"),
          value: "tag",
          class: "text-uppercase text-truncate",
          sort: (a, b) => {
            if (this.sortByCategoryLocation) {
              if (a.category == "location" && b.category == "location")
                return 0;
              if (a.category == "location" && b.category != "location")
                return -1;
              if (a.category != "location" && b.category == "location")
                return 1;
            }

            return a.name.localeCompare(b.name);
          },
        },
        {
          text: this.$i18n.t("profile.mentorship.column.start"),
          value: "startedAt",
          class: "text-uppercase text-truncate",
        },

        {
          text: this.$i18n.t("profile.mentorship.column.duration"),
          value: "duration",
          class: "text-uppercase text-truncate",
          align: "right",
        },

        {
          text: this.$i18n.t("profile.mentorship.column.status"),
          value: "status",
          class: "text-uppercase text-truncate",
        },
        {
          text: this.$i18n.t("profile.mentorship.column.action"),
          value: "actions",
          class: "text-uppercase text-truncate",
          sortable: false,
        },
        {
          text: this.$i18n.t("profile.mentorship.column.learningActions"),
          value: "learningActions",
          class: "text-uppercase text-truncate",
          sortable: false,
        },
        {
          text: this.$i18n.t("profile.mentorship.column.feedback"),
          value: "feedbacks",
          class: "text-uppercase text-truncate",
          sortable: false,
        },

        { text: "", value: "menu", sortable: false, align: "right" },
      ];
      if (this.mentorView) {
        h.splice(3, 0, {
          text: this.$i18n.t("profile.mentorship.column.mentee"),
          value: "employee",
          class: "text-uppercase text-truncate",
        });
      } else {
        h.splice(2, 0, {
          text: this.$i18n.t("profile.mentorship.column.endsAt"),
          value: "endsAt",
          class: "text-uppercase text-truncate",
        });
        h.splice(4, 0, {
          text: this.$i18n.t("profile.mentorship.column.mentor"),
          value: "mentor",
          class: "text-uppercase text-truncate",
        });
      }
      if (this.showMentorColumn && this.mentorView) {
        h.splice(3, 0, {
          text: this.$i18n.t("profile.mentorship.column.mentor"),
          value: "mentor",
          class: "text-uppercase text-truncate",
        });
      }

      return h;
    },

    items() {
      if (this.mentorships) {
        return this.mentorships.map((mentorship) => {
          const fromOldAccount =
            this.employee && mentorship.employee.id !== this.employee.id;

          const signatureState = getSignatureState(
            mentorship.signatures,
            this.revStatuses
          );
          const mentorshipActionsStats = getMentorshipActionsStats(
            mentorship.actions
          );

          return {
            ...mentorship,
            fromOldAccount,
            mentorshipActionsStats,
            ...signatureState,
          };
        });
      }

      return [];
    },
  },

  methods: {
    isFeedbackSubmitted(item) {
      return (
        !item.feedbacks ||
        item.feedbacks.length == 0 ||
        (item.feedbacks.length > 0 &&
          !!item.feedbacks[item.feedbacks.length - 1].menteeSubmittedForm &&
          !!item.feedbacks[item.feedbacks.length - 1].mentorSubmittedForm)
      );
    },
    changeAssignedFeedbacks(item, toAssignMentorshipFeedbackIds) {
      if (!item.id) {
        return;
      }
      if (!toAssignMentorshipFeedbackIds) {
        this.feedbacksChange = {
          show: true,
          assignedFeedbackForms: item.feedbacks || [],
          tag: item.tag,
          callback: (toAssignMentorshipFeedbackIds) =>
            this.changeAssignedFeedbacks(item, toAssignMentorshipFeedbackIds),
        };
      } else {
        this.$apollo
          .mutate({
            mutation: ChangeAssignedMentorshipFeedbackFormsQuery,
            variables: {
              mentorshipId: item.id,
              toAssignMentorshipFeedbackIds,
            },
          })
          .then((res) => {
            this.handleFeedbacksChanged(item, res.data.mentorshipFeedbacks);
          })
          .catch((err) => {
            this.$store.commit("push_global_message", {
              color: "error",
              text: err,
            });
          })
          .finally(() => {
            this.feedbacksChange = {
              show: false,
              assignedFeedbackForms: [],
              tag: null,
              callback: () => {},
            };
          });
      }
    },

    handleFeedbacksChanged(item, feedbacks) {
      item.feedbacks = feedbacks;
    },
    assignMentorshipActionVersion(item) {
      this.assignActionVersion = {
        id: item.id,
        tagId: item.tag.id,
        assignedVersions: (item.actions && item.actions.assignedVersions) || [],
      };
    },
    handleAssignActionVersionDone(isUpdated) {
      this.assignActionVersion = null;
      if (isUpdated) {
        this.$emit("refresh");
      }
    },
    changeTag(item, id) {
      if (typeof id === "undefined") {
        this.tagChange = {
          show: true,
          oldTag: item.tag,
          callback: (id) => this.changeTag(item, id),
        };
      } else {
        this.$apollo
          .mutate({
            mutation: ChangeMentorshipTagQuery,
            variables: {
              employeeId: this.id,
              oldTagId: item.tag.id,
              newTagId: id,
            },
          })
          .then(() => {
            this.$emit("refresh");
          })
          .catch((err) => {
            this.$store.commit("push_global_message", {
              color: "error",
              text: err,
            });
          })
          .finally(() => {
            this.tagChange = {
              show: false,
              oldTag: null,
              callback: () => {},
            };
          });
      }
    },

    changeStartedAt(item, newStartedAt) {
      if (!newStartedAt) {
        this.changeStartedAtDialog = {
          show: true,
          date: item.startedAt,
          callback: (newStartedAt) => this.changeStartedAt(item, newStartedAt),
        };
      } else {
        this.$apollo
          .mutate({
            mutation: ChangeMentorshipStartedAt,
            variables: {
              mentorshipId: item.id,
              startedAt: newStartedAt,
            },
          })
          .then(() => {
            this.$emit("refresh");
          })
          .catch(() => {
            this.$store.commit("push_global_message", {
              color: "error",
              text: this.$t("error.saveFailed"),
            });
          });
      }
    },

    changeEndsAt(item, newEndsAt) {
      if (!newEndsAt) {
        this.changeEndsAtDialog = {
          show: true,
          date: item.endsAt,
          callback: (newEndsAt) => this.changeEndsAt(item, newEndsAt),
        };
      } else {
        this.$apollo
          .mutate({
            mutation: ChangeMentorshipEndsAt,
            variables: {
              mentorshipId: item.id,
              endsAt: newEndsAt,
            },
          })
          .then((res) => {
            item.endsAt = res.data.mentorship.endsAt;
          })
          .catch(() => {
            this.$store.commit("push_global_message", {
              color: "error",
              text: this.$t("error.saveFailed"),
            });
          });
      }
    },

    sign(item, status, args) {
      if (!args && !this.flow[status].noPassword) {
        this.impersonateDialog = {
          callback: (args) => this.sign(item, status, args),
          status,
          show: true,
        };
      } else {
        this.impersonateDialog = {
          callback: () => {},
          show: false,
        };
        this.$apollo
          .mutate({
            mutation: this.flow[status].query,
            variables: {
              mentorshipId: item.id,
              password: args && args.password,
            },
          })
          .then(() => {
            this.$emit("refresh");
          })
          .catch((err) => {
            this.$store.commit("push_global_message", {
              color: "error",
              text: err,
            });
          });
      }
    },

    revoke(signature, args) {
      if (!args) {
        this.revokeDialog = {
          callback: (args) => this.revoke(signature, args),
          status: signature.type,
          show: true,
        };
      } else {
        this.revokeDialog = {
          callback: () => {},
          status: null,
          show: false,
        };
        this.$apollo
          .mutate({
            mutation: RevokeSignatureQuery,
            variables: {
              signatureId: signature.id,
              reason: args.reason,
            },
          })
          .then((res) => {
            signature.signatures = res.data.signature.signatures;
          })
          .catch((err) => {
            this.$store.commit("push_global_message", {
              color: "error",
              text: err,
            });
          });
      }
    },

    deleteConfirmation(signature, args) {
      if (!args) {
        this.revokeDialog = {
          callback: (args) => this.deleteConfirmation(signature, args),
          status: signature.type,
          show: true,
        };
      } else {
        this.revokeDialog = {
          callback: () => {},
          status: null,
          show: false,
        };

        this.$apollo
          .mutate({
            mutation: DeleteMentorshipConfirmationQuery,
            variables: {
              signatureId: signature.id,
              reason: args.reason,
            },
          })
          .then((res) => {
            signature.signatures = res.data.signature.signatures;
            this.$emit("refresh");
          })
          .catch((err) => {
            this.$store.commit("push_global_message", {
              color: "error",
              text: err,
            });
          });
      }
    },

    fail(item, args) {
      if (!args) {
        this.failDialog = {
          callback: (args) => this.fail(item, args),
          show: true,
        };
      } else {
        this.failDialog = {
          callback: () => {},
          show: false,
        };
        this.$apollo
          .mutate({
            mutation: FailMentorshipQuery,
            variables: {
              mentorshipId: item.id,
              reason: args.reason,
            },
          })
          .then(() => {
            this.$emit("refresh");
          })
          .catch((err) => {
            this.$store.commit("push_global_message", {
              color: "error",
              text: err,
            });
          });
      }
    },

    changeMentor(item, mentorId) {
      if (typeof mentorId === "undefined") {
        this.mentorSelector = {
          show: true,
          callback: (mentorId) => this.changeMentor(item, mentorId),
        };
      } else {
        this.mentorSelector = {
          show: false,
          callback: () => {},
        };
        this.$apollo
          .mutate({
            mutation: ChangeMentorshipMentorQuery,
            variables: {
              mentorshipId: item.id,
              mentorId,
            },
          })
          .then(() => {
            this.$emit("refresh");
          })
          .catch((err) => {
            this.$store.commit("push_global_message", {
              color: "error",
              text: err,
            });
          });
      }
    },

    deactivate(item, args) {
      if (!args) {
        this.deactivateDialog = {
          callback: (args) => this.deactivate(item, args),
          status: item.id,
          item: item,
          show: true,
        };
      } else {
        this.deactivateDialog = {
          callback: () => {},
          status: null,
          item: null,
          show: false,
        };

        this.$apollo
          .mutate({
            mutation: DeactivateMentorshipQuery,
            variables: {
              id: item.id,
              reason: args.reason,
            },
          })
          .then(() => {
            this.$emit("refresh");
          })
          .catch((err) => {
            this.$store.commit("push_global_message", {
              color: "error",
              text: err,
            });
          });
      }
    },

    isPreviousFeedbackStageSubmitted(feedbacks, index) {
      if (index == 0) {
        return true;
      }
      return (
        feedbacks &&
        feedbacks.length > 0 &&
        feedbacks[index - 1] &&
        !!feedbacks[index - 1].mentorSubmittedForm &&
        !!feedbacks[index - 1].menteeSubmittedForm
      );
    },
    isConfirmed(item) {
      return (
        item.signature &&
        item.signature.type === "confirm" &&
        !item.signature.revoke
      );
    },
  },
  apollo: {
    myOldAccounts: {
      query: MyOldAccountsQuery,
    },
  },
};
</script>
